import { detectAiSource } from './aiTraffic';

/**
 * First-touch acquisition attribution, captured client-side and stored on the
 * server with the guest session or user record.
 *
 * Why server-side storage rather than an analytics tag: Clarity has no revenue
 * concept and GA4 is gone, so "which channel brings paying users" has no answer
 * from analytics alone. Persisting the source next to the account lets it join
 * straight to payment_transactions. It also survives ad-blockers and the
 * `noreferrer` that AI engines use, neither of which a third-party tag does.
 *
 * FIRST-touch, deliberately: someone may arrive from ChatGPT, leave, and come
 * back directly a week later to pay. Last-touch would credit "direct" and hide
 * the channel that actually earned the customer.
 */

export type AcquisitionChannel =
  | 'ai_assistant'
  | 'organic_search'
  | 'social'
  | 'referral'
  | 'direct';

export interface Acquisition {
  /** Normalised, low-cardinality id: 'chatgpt', 'google', 'direct', … */
  source: string;
  channel: AcquisitionChannel;
  /** Landing path only — never the query string, which can carry personal data. */
  landingPath: string;
}

const SEARCH_ENGINES: Record<string, string> = {
  'google.com': 'google',
  'google.co.in': 'google',
  'bing.com': 'bing',
  'duckduckgo.com': 'duckduckgo',
  'yahoo.com': 'yahoo',
  'ecosia.org': 'ecosia',
  'brave.com': 'brave',
  'yandex.com': 'yandex',
};

const SOCIAL: Record<string, string> = {
  'facebook.com': 'facebook',
  'instagram.com': 'instagram',
  'x.com': 'x',
  'twitter.com': 'x',
  'linkedin.com': 'linkedin',
  'reddit.com': 'reddit',
  'youtube.com': 'youtube',
  'pinterest.com': 'pinterest',
  'whatsapp.com': 'whatsapp',
  't.co': 'x',
};

const matches = (host: string, domain: string) => host === domain || host.endsWith(`.${domain}`);

function lookup(host: string, table: Record<string, string>): string | null {
  for (const [domain, id] of Object.entries(table)) {
    if (matches(host, domain)) return table[domain] ?? id;
  }
  return null;
}

/** Pure classification, so it can be tested without a browser. */
export function detectAcquisition(url: string, referrer: string, selfHost: string): Acquisition {
  let landingPath = '/';
  try {
    landingPath = new URL(url).pathname || '/';
  } catch {
    // keep the default
  }
  // Guard against unbounded values reaching the database.
  landingPath = landingPath.slice(0, 255);

  // AI engines first: they're the reason this exists, and some of them also
  // carry a referrer that would otherwise be classified as something else.
  const ai = detectAiSource(url, referrer);
  if (ai) return { source: ai.id, channel: 'ai_assistant', landingPath };

  let host = '';
  try {
    host = referrer ? new URL(referrer).hostname.toLowerCase() : '';
  } catch {
    host = '';
  }

  // Our own pages aren't an acquisition source.
  if (!host || matches(host, selfHost)) return { source: 'direct', channel: 'direct', landingPath };

  const search = lookup(host, SEARCH_ENGINES);
  if (search) return { source: search, channel: 'organic_search', landingPath };

  const social = lookup(host, SOCIAL);
  if (social) return { source: social, channel: 'social', landingPath };

  return { source: host.replace(/^www\./, '').slice(0, 64), channel: 'referral', landingPath };
}

const STORAGE_KEY = 'kundaliai_acquisition';

/**
 * The first-touch acquisition for this visitor, detected on their first landing
 * page and remembered across visits.
 *
 * localStorage rather than sessionStorage: the whole point is to still know the
 * original source when someone converts on a later visit.
 */
export function resolveAcquisition(): Acquisition | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored) as Acquisition;
  } catch {
    // Blocked storage — fall through and detect fresh each time.
  }

  const detected = detectAcquisition(window.location.href, document.referrer, window.location.hostname);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(detected));
  } catch {
    // Private mode: attribution just won't persist across visits.
  }
  return detected;
}
